export interface SubprojectBreakdownState {
  subprojectId: string;
  ownerAmountFiscalyear: string;
  contractorAmountFiscalyear: string;
  ownerAmountTotal: string;
  contractorAmountTotal: string;
}

export interface LineItemState {
  /** Local UUID used as stable React key */
  clientId: string;
  /** Backend PK — present after first save */
  id?: number;
  section: 'capital' | 'operation';
  category: string;
  /** Display label for custom rows */
  label: string;
  /** Stored as string to allow empty/partial input */
  ownerAmountFiscalyear: string;
  contractorAmountFiscalyear: string;
  ownerAmountTotal: string;
  contractorAmountTotal: string;
  sortOrder: number;
  subprojectBreakdowns: SubprojectBreakdownState[];
}

export interface CostEstimateState {
  id?: number;
  dataSource: string;
  referenceYear: string;
  rateInflationOwner: string;
  rateInflationLand: string;
  rateInflationCostEscalation: string;
  localCostMultiplier: string;
  /** 'local' | 'national' | '' (not stated) */
  costBasis: string;
  lineItems: LineItemState[];
}

// Fixed capital categories in display order
export const CAPITAL_CATEGORIES: {
  category: string;
  label: string;
  optional: boolean;
  sortOrder: number;
  hasBreakdown: boolean;
  /** Optional one-line note shown under the row label */
  hint?: string;
}[] = [
  {
    category: 'planning_design',
    label: 'Planning, Design & Supervision',
    optional: false,
    sortOrder: 0,
    hasBreakdown: false,
    hint: 'Budget line only — passive system cost estimates use the Design Cost entered in Space Usage.',
  },
  {
    category: 'land_cost',
    label: 'Land Cost & Land Acquisition',
    optional: true,
    sortOrder: 1,
    hasBreakdown: false,
  },
  {
    category: 'site_improvement',
    label: 'Site Improvement & Utility Costs',
    optional: true,
    sortOrder: 2,
    hasBreakdown: false,
  },
  {
    category: 'contingency',
    label: 'Contingency, Risk, Escalations & Claims',
    optional: false,
    sortOrder: 3,
    hasBreakdown: false,
  },
  {
    category: 'construction',
    label: 'Construction (Total)',
    optional: false,
    sortOrder: 4,
    hasBreakdown: true,
  },
];

// Fixed operation categories in display order
export const OPERATION_CATEGORIES: {
  category: string;
  label: string;
  optional: boolean;
  sortOrder: number;
  hasBreakdown: boolean;
}[] = [
  {
    category: 'personnel',
    label: 'Annual Operating Personnel Cost',
    optional: false,
    sortOrder: 5,
    hasBreakdown: false,
  },
  {
    category: 'minor_maintenance',
    label: 'Annual Minor Maintenance Cost',
    optional: false,
    sortOrder: 6,
    hasBreakdown: false,
  },
  {
    category: 'water_utility',
    label: 'Annual Water Utility Cost',
    optional: false,
    sortOrder: 7,
    hasBreakdown: true,
  },
  {
    category: 'energy_utility',
    label: 'Annual Energy Utility Cost',
    optional: false,
    sortOrder: 8,
    hasBreakdown: true,
  },
];

export function parseAmount(s: string): number | null {
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

export function initLineItems(
  section: 'capital' | 'operation',
): LineItemState[] {
  const categories =
    section === 'capital' ? CAPITAL_CATEGORIES : OPERATION_CATEGORIES;
  return categories.map((cat) => ({
    clientId: crypto.randomUUID(),
    section,
    category: cat.category,
    label: '',
    ownerAmountFiscalyear: '',
    contractorAmountFiscalyear: '',
    ownerAmountTotal: '',
    contractorAmountTotal: '',
    sortOrder: cat.sortOrder,
    subprojectBreakdowns: [],
  }));
}

// local_cost_multiplier is DecimalField(max_digits=5, decimal_places=3) with
// MinValueValidator(0.001) in server/csisite/api/cost_estimates/models.py
// (source of truth): a plain positive decimal, at most 2 integer digits and 3
// decimal places, i.e. 0.001 to 99.999. Zero would zero every cost and a
// negative multiplier would produce negative dollars.
const LOCAL_COST_MULTIPLIER_PATTERN = /^(\d{1,2}(\.\d{0,3})?|\.\d{1,3})$/;
const LOCAL_COST_MULTIPLIER_MESSAGE =
  '"Local Cost Multiplier" must be a number greater than 0 and at most 99.999, with up to 3 decimal places.';

export function validateLocalCostMultiplier(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!LOCAL_COST_MULTIPLIER_PATTERN.test(trimmed) || Number(trimmed) <= 0) {
    return LOCAL_COST_MULTIPLIER_MESSAGE;
  }
  return null;
}
